import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import { getEventTypeDescription, getEventTypeLabel } from "../shared/utils";
import { RENTALS_TYPES, type LeadEventType, type RentalsType } from "../shared/types";
import {
  HUBSPOT_EVENT_TARGET_POSITION,
  HUBSPOT_MAPPABLE_EVENTS,
  type HubspotEventMapping,
  type HubspotPipeline,
  type HubspotStage,
} from "./types";

type EventState = {
  enabled: boolean;
  pipelineId: string;
  dealStage: string;
  createDeal: boolean;
};

// Second config axis (besides rentalsType): a Palomma pipeline. Both surface the
// exact same event-mapping editor; only the discriminator threaded into
// `createCrmConfiguration` differs (`rentalsType` vs `palommaPipelineId`).
// Contact-field mapping is NOT here — it is merchant-wide, in "Variables globales".
type EditingTarget = { kind: "rentalsType" | "pipeline"; id: string; name: string };

type HubspotConfigSummary = {
  id: string;
  hubspotEventsMapping: HubspotEventMapping[];
  hubspotLeadNamePrefix?: string;
};

type TargetWithHubspotConfig = {
  id: string;
  name: string;
  crmConfiguration?: HubspotConfigSummary;
};

// Palomma pipelines already routed by the rentalsType section (`default`) or that are
// machine-only (`auth`, OTP delivery) are hidden — see core/pipeline/types.ts.
const HIDDEN_PIPELINE_IDS = new Set(["default", "auth"]);

const DEFAULT_PIPELINE_ID = "default";

// Stand-in lead for the name-prefix preview. Two words on purpose: the prefix lands
// on the first name only, so a single-word sample would hide that the surname comes
// through untouched. Kept obviously fictitious — a real-looking name reads as data.
const NAME_PREVIEW_SAMPLE = "Pepito Pérez";

// Fallback for the rentalsType (Comercial) section when the merchant's stored
// `default` pipeline row can't be read: the position statuses of the standard
// Comercial pipeline (core/pipeline DEFAULT_POSITIONS). Keeps the captación /
// mantenimiento events hidden from Comercial even if the row is momentarily absent.
const DEFAULT_COMMERCIAL_POSITION_STATUSES = new Set([
  "redirected",
  "appointmentIntent",
  "appointmentPending",
  "inactive",
  "discarded",
]);

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

const emptyEventState = (): EventState => ({
  enabled: false,
  pipelineId: "",
  dealStage: "",
  createDeal: true,
});

const toHubspotConfigSummary = (config: {
  id: string;
  hubspotEventsMapping?: unknown;
  hubspotLeadNamePrefix?: string;
}): HubspotConfigSummary => ({
  id: config.id,
  hubspotEventsMapping: (config.hubspotEventsMapping || []) as HubspotEventMapping[],
  hubspotLeadNamePrefix: config.hubspotLeadNamePrefix,
});

const HubspotCrmConfiguration = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);

  const { data: crmConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const {
    data: crmConfigurations,
    isLoading: isLoadingConfigurations,
    refetch: refetchConfigurations,
  } = trpc.crmSelfConfig.listCrmConfigurations.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const { data: palommaPipelines } = trpc.pipeline.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  // Positions per Palomma pipeline — drives which events each pipeline can fire.
  const { data: palommaPipelinesFull } = trpc.pipeline.listFull.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const accessToken: string | undefined = (crmConnection as any)?.hubspot?.accessToken;

  const hubspotConfigurations = useMemo(
    () => (crmConfigurations || []).filter((config) => config.provider === "hubspot"),
    [crmConfigurations]
  );

  // A Tipo de Negocio shows ONLY its own discriminator-scoped row. The merchant's
  // `isFallback` row is deliberately NOT substituted in: `resolveCrmConfiguration`'s
  // rentals branch never consults `isFallback` (only the `development` branch and
  // the `internal` case do), so labelling a card "Configuración heredada (fallback)"
  // promised an inheritance that does not happen at runtime — the merchant saw
  // "inherited" and got silence. An unconfigured type now reads "Pendiente".
  const rentalsTypesWithConfig = useMemo<TargetWithHubspotConfig[]>(() => {
    if (merchantType !== "rentals") return [];
    return RENTALS_TYPES.map((rentalsType) => {
      const config = hubspotConfigurations.find((c) => c.rentalsType === rentalsType.id);
      return {
        id: rentalsType.id,
        name: rentalsType.name,
        crmConfiguration: config ? toHubspotConfigSummary(config) : undefined,
      };
    });
  }, [hubspotConfigurations, merchantType]);

  // "Configuración por Pipeline" — the second axis. Lists non-default Palomma pipelines
  // (captación, custom projects, …) matched on the exact `palommaPipelineId` discriminator.
  // No fallback here: a pipeline config applies only to its own pipeline.
  const pipelinesWithConfig = useMemo<TargetWithHubspotConfig[]>(() => {
    if (merchantType !== "rentals" || !palommaPipelines) return [];
    return palommaPipelines
      .filter((pipeline) => !HIDDEN_PIPELINE_IDS.has(pipeline.id))
      .map((pipeline) => {
        const config = hubspotConfigurations.find((c) => c.palommaPipelineId === pipeline.id);
        return {
          id: pipeline.id,
          name: pipeline.name,
          crmConfiguration: config ? toHubspotConfigSummary(config) : undefined,
        };
      });
  }, [palommaPipelines, hubspotConfigurations, merchantType]);

  const [pipelines, setPipelines] = useState<HubspotPipeline[]>([]);
  const [stagesByPipeline, setStagesByPipeline] = useState<Record<string, HubspotStage[]>>({});
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);
  const [editingTarget, setEditingTarget] = useState<EditingTarget | null>(null);
  const [eventState, setEventState] = useState<Record<string, EventState>>({});
  const [namePrefix, setNamePrefix] = useState("");

  const utils = trpc.useUtils();

  // Resolve the config row for whichever axis is being edited.
  const currentConfiguration = useMemo<HubspotConfigSummary | undefined>(() => {
    if (!editingTarget) return undefined;
    const source =
      editingTarget.kind === "rentalsType" ? rentalsTypesWithConfig : pipelinesWithConfig;
    return source.find((t) => t.id === editingTarget.id)?.crmConfiguration;
  }, [editingTarget, rentalsTypesWithConfig, pipelinesWithConfig]);

  // Flatten each pipeline's positions into a set of statuses, keyed by pipeline id.
  const positionStatusesByPipeline = useMemo<Record<string, Set<string>>>(() => {
    const map: Record<string, Set<string>> = {};
    (palommaPipelinesFull || []).forEach((pipeline) => {
      const statuses = new Set<string>();
      Object.values(pipeline.positions ?? {}).forEach((positions) =>
        (positions as Array<{ id: string; status?: string }>).forEach((position) =>
          statuses.add(position.status ?? position.id)
        )
      );
      map[pipeline.id] = statuses;
    });
    return map;
  }, [palommaPipelinesFull]);

  // The subset of mappable events relevant to the target being edited: only events
  // whose target position exists in that pipeline (rentalsType → Comercial/default).
  // While positions are still loading (statuses unknown) we show all — never hide by
  // mistake. Every pipeline has `redirected` + `inactive`, so this is never empty.
  const mappableEvents = useMemo<readonly LeadEventType[]>(() => {
    if (!editingTarget) return HUBSPOT_MAPPABLE_EVENTS;
    const statuses =
      editingTarget.kind === "pipeline"
        ? positionStatusesByPipeline[editingTarget.id]
        : (positionStatusesByPipeline[DEFAULT_PIPELINE_ID] ??
          DEFAULT_COMMERCIAL_POSITION_STATUSES);
    if (!statuses) return HUBSPOT_MAPPABLE_EVENTS;
    return HUBSPOT_MAPPABLE_EVENTS.filter((event) =>
      statuses.has(HUBSPOT_EVENT_TARGET_POSITION[event])
    );
  }, [editingTarget, positionStatusesByPipeline]);

  const getPipelines = trpc.crmSelfConfig.getPipelines.useMutation({
    onSuccess: (result) => {
      if (result.success && result.pipelines) {
        setPipelines(result.pipelines as HubspotPipeline[]);
      } else if (!result.success) {
        toast.error("Error", { description: result.error || "No se pudieron obtener los pipelines" });
      }
      setIsLoadingPipelines(false);
    },
    onError: (error) => {
      toast.error("Error", { description: error.message || "Error al obtener los pipelines" });
      setIsLoadingPipelines(false);
    },
  });

  const getStages = trpc.crmSelfConfig.getStages.useMutation({
    onSuccess: (result, variables) => {
      if (result.success && result.stages) {
        setStagesByPipeline((prev) => ({
          ...prev,
          [String(variables.pipelineId)]: result.stages as HubspotStage[],
        }));
      } else if (!result.success) {
        toast.error("Error", { description: result.error || "No se pudieron obtener las etapas" });
      }
    },
    onError: (error) => {
      toast.error("Error", { description: error.message || "Error al obtener las etapas" });
    },
  });

  const createConfiguration = trpc.crmSelfConfig.createCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración creada", {
        description: "La configuración de HubSpot se guardó correctamente.",
      });
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("Error", { description: error.message || "Error al guardar la configuración." });
    },
  });

  const updateConfiguration = trpc.crmSelfConfig.updateCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración actualizada", {
        description: "La configuración de HubSpot se actualizó correctamente.",
      });
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("Error", { description: error.message || "Error al actualizar la configuración." });
    },
  });

  // Fetch pipelines once, when the token is available. Contact PROPERTIES are not
  // fetched here any more — this card no longer maps fields; "Variables globales"
  // does, and it fetches them itself.
  useEffect(() => {
    if (!accessToken) return;
    if (pipelines.length === 0 && !isLoadingPipelines) {
      setIsLoadingPipelines(true);
      getPipelines.mutate({ provider: "hubspot", apiKey: accessToken });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Load the selected target's stored mappings into local state.
  useEffect(() => {
    if (!editingTarget) {
      setEventState({});
      setNamePrefix("");
      return;
    }
    setNamePrefix(currentConfiguration?.hubspotLeadNamePrefix ?? "");
    const state: Record<string, EventState> = {};
    for (const event of mappableEvents) {
      const mapping = currentConfiguration?.hubspotEventsMapping.find((m) => m.event === event);
      state[event] = mapping
        ? {
            enabled: true,
            pipelineId: mapping.pipelineId,
            dealStage: mapping.dealStage,
            createDeal: mapping.createDeal ?? true,
          }
        : emptyEventState();
    }
    setEventState(state);
  }, [editingTarget, currentConfiguration, mappableEvents]);

  // Lazily fetch stages for any pipeline referenced by the current event state.
  useEffect(() => {
    if (!accessToken) return;
    const pipelineIds = new Set(
      Object.values(eventState)
        .filter((s) => s.enabled && s.pipelineId)
        .map((s) => s.pipelineId)
    );
    pipelineIds.forEach((pipelineId) => {
      if (!stagesByPipeline[pipelineId]) {
        getStages.mutate({ provider: "hubspot", apiKey: accessToken, pipelineId });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventState, accessToken]);

  const updateEvent = (event: string, patch: Partial<EventState>) => {
    setEventState((prev) => ({
      ...prev,
      [event]: { ...(prev[event] || emptyEventState()), ...patch },
    }));
  };

  const handlePipelineChange = (event: string, pipelineId: string) => {
    updateEvent(event, { pipelineId, dealStage: "" });
    if (accessToken && pipelineId && !stagesByPipeline[pipelineId]) {
      getStages.mutate({ provider: "hubspot", apiKey: accessToken, pipelineId });
    }
  };

  const handleSave = async () => {
    if (!merchantId || !editingTarget) {
      toast.warning("Selecciona una configuración", {
        description: "Elige un tipo de negocio o pipeline para configurar HubSpot.",
      });
      return;
    }

    const hubspotEventsMapping: HubspotEventMapping[] = [];
    for (const event of mappableEvents) {
      const state = eventState[event];
      if (!state?.enabled) continue;
      if (!state.pipelineId || !state.dealStage) {
        toast.error("Campos requeridos", {
          description: `Para el evento "${getEventTypeLabel(event)}", selecciona pipeline y etapa.`,
        });
        return;
      }
      hubspotEventsMapping.push({
        event,
        pipelineId: state.pipelineId,
        dealStage: state.dealStage,
        createDeal: state.createDeal,
      });
    }

    // NOTE: `hubspotContactFieldMappings` is deliberately NOT sent — not even as
    // `[]`. Contact-field mapping moved to the connection ("Variables globales"),
    // but legacy per-config rows are still READ and merged by the sync, and sending
    // `[]` here would wipe them. The Zod field is `.optional()` and the entity only
    // writes when `!== undefined`, so omitting it is a genuine no-op.

    // With no fallback substitution, `currentConfiguration` is always the target's
    // OWN row — so the choice is simply "does a row exist yet".
    if (currentConfiguration) {
      await updateConfiguration.mutateAsync({
        merchantId,
        id: currentConfiguration.id,
        hubspotEventsMapping,
        // Always sent, including as "" — that empty string is how a merchant clears
        // a prefix. Omitting it would make the field impossible to unset.
        hubspotLeadNamePrefix: namePrefix,
      });
    } else {
      await createConfiguration.mutateAsync({
        merchantId,
        provider: "hubspot",
        enabled: true,
        // Thread whichever discriminator this target represents (create-only, immutable).
        ...(editingTarget.kind === "rentalsType"
          ? { rentalsType: editingTarget.id as RentalsType }
          : { palommaPipelineId: editingTarget.id }),
        hubspotEventsMapping,
        hubspotLeadNamePrefix: namePrefix,
      });
    }
  };

  if (isLoadingConfigurations) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p className="text-sm text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  if (!crmConnection || crmConnection.provider !== "hubspot") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">Conecta HubSpot para configurar los eventos.</p>
      </div>
    );
  }

  if (merchantType !== "rentals") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">
          La configuración de HubSpot está disponible para comercios de tipo arriendos.
        </p>
      </div>
    );
  }

  const renderTargetCards = (targets: TargetWithHubspotConfig[], kind: EditingTarget["kind"]) => (
    <div className="flex flex-col gap-3">
      {targets.map((target) => {
        const configuredEvents = target.crmConfiguration?.hubspotEventsMapping || [];
        const isConfigured = configuredEvents.length > 0;
        const isSelected = editingTarget?.kind === kind && editingTarget.id === target.id;
        const eventsSummary = isConfigured
          ? configuredEvents.map((m) => getEventTypeLabel(m.event)).join(", ")
          : "Sin eventos configurados";
        return (
          <div
            key={target.id}
            className={`rounded-md border p-4 ${
              isSelected ? "border-purple-200 bg-purple-50/25" : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold">{target.name}</p>
                <p className="text-xs text-gray-500">{eventsSummary}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  className={
                    isConfigured
                      ? "bg-green-100 text-green-800 border-green-200"
                      : "bg-yellow-100 text-yellow-800 border-yellow-200"
                  }
                >
                  {isConfigured ? "Configurado" : "Pendiente"}
                </Badge>
                <Button
                  size="sm"
                  variant={isSelected ? "default" : "outline"}
                  onClick={() => setEditingTarget({ kind, id: target.id, name: target.name })}
                >
                  {isConfigured ? "Editar" : "Configurar"}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-6">
        <div>
          <p className="mb-2 text-lg font-bold">Acciones HubSpot</p>
          <p className="text-sm text-gray-600">
            Configura a qué etapa de negocio mover el contacto en HubSpot cuando ocurra cada evento
            final del lead.
          </p>
        </div>

        <div>
          <p className="mb-1 text-sm font-semibold">Configuración por Tipo de Negocio</p>
          <p className="mb-4 text-xs text-gray-500">
            Selecciona el tipo de negocio para el que deseas configurar el mapeo de eventos.
          </p>
          {renderTargetCards(rentalsTypesWithConfig, "rentalsType")}
        </div>

        {pipelinesWithConfig.length > 0 && (
          <div>
            <p className="mb-1 text-sm font-semibold">Configuración por Pipeline</p>
            <p className="mb-4 text-xs text-gray-500">
              Para leads sin propiedad asociada (captación, proyectos personalizados), configura el
              mapeo de eventos según el pipeline de Palomma del lead.
            </p>
            {renderTargetCards(pipelinesWithConfig, "pipeline")}
          </div>
        )}

        {editingTarget ? (
          <>
            <div className="flex flex-col gap-2">
              <div>
                <p className="text-sm font-semibold">Prefijo del nombre · {editingTarget.name}</p>
                <p className="text-xs text-gray-500">
                  Texto que se antepone al nombre del contacto en HubSpot, solo para los leads que
                  pasen por esta configuración. Sirve para distinguir de dónde viene cada contacto.
                  Déjalo vacío si no lo necesitas.
                </p>
              </div>
              <div className="flex max-w-md flex-col gap-1">
                <Label className="text-xs">Prefijo</Label>
                <Input
                  value={namePrefix}
                  onChange={(e) => setNamePrefix(e.target.value)}
                  placeholder="Ej: Arriendo_Residencial_"
                  maxLength={60}
                />
                <p className="text-xs text-gray-500">
                  {namePrefix.trim() ? (
                    <>
                      Un lead llamado "{NAME_PREVIEW_SAMPLE}" se creará como{" "}
                      <span className="font-semibold text-gray-700">
                        {namePrefix}
                        {NAME_PREVIEW_SAMPLE}
                      </span>
                      .
                    </>
                  ) : (
                    "Sin prefijo, el contacto se crea con el nombre tal como lo dio el lead."
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  Se aplica solo al contacto — el negocio conserva el nombre real. Un lead que
                  todavía no ha dicho su nombre no recibe el prefijo solo: se escribe cuando el
                  nombre exista.
                </p>
              </div>
            </div>

            <div>
              <p className="mb-3 text-sm font-semibold">Mapeo de eventos · {editingTarget.name}</p>
              {isLoadingPipelines ? (
                <div className="flex items-center gap-2">
                  <Spinner size="sm" />
                  <p className="text-xs text-gray-500">Cargando pipelines...</p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {mappableEvents.map((event) => {
                    const state = eventState[event] || emptyEventState();
                    const stages = state.pipelineId ? stagesByPipeline[state.pipelineId] : undefined;
                    return (
                      <div
                        key={event}
                        className="rounded-md border border-gray-200 bg-gray-50 p-3"
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={state.enabled}
                            onCheckedChange={(checked) =>
                              updateEvent(event, { enabled: checked as boolean })
                            }
                          />
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help text-xs font-semibold text-blue-600">
                                  {getEventTypeLabel(event)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>{getEventTypeDescription(event)}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>

                        {event === "lead.created" && (
                          <p className="ml-6 mt-1 text-xs text-gray-500">
                            El lead aún no tiene preferencias al crearse; esos campos se llenan en
                            los eventos siguientes.
                          </p>
                        )}

                        {state.enabled && (
                          <div className="ml-6 mt-3 flex flex-col gap-2">
                            <div className="flex flex-col gap-1">
                              <Label className="text-xs">
                                Pipeline <span className="text-red-500">*</span>
                              </Label>
                              <select
                                className={selectClass}
                                value={state.pipelineId}
                                onChange={(e) => handlePipelineChange(event, e.target.value)}
                              >
                                <option value="">Selecciona un pipeline</option>
                                {pipelines.map((pipeline) => (
                                  <option key={pipeline.id} value={pipeline.id}>
                                    {pipeline.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="flex flex-col gap-1">
                              <Label className="text-xs">
                                Etapa del negocio <span className="text-red-500">*</span>
                              </Label>
                              <select
                                className={selectClass}
                                value={state.dealStage}
                                onChange={(e) => updateEvent(event, { dealStage: e.target.value })}
                                disabled={!state.pipelineId || !stages}
                              >
                                {state.pipelineId && stages ? (
                                  <>
                                    <option value="">Selecciona una etapa</option>
                                    {stages.map((stage) => (
                                      <option key={stage.id} value={stage.id}>
                                        {stage.name}
                                      </option>
                                    ))}
                                  </>
                                ) : (
                                  <option value="">Selecciona primero un pipeline</option>
                                )}
                              </select>
                            </div>

                            <label className="flex items-center gap-2 text-xs">
                              <Checkbox
                                checked={state.createDeal}
                                onCheckedChange={(checked) =>
                                  updateEvent(event, { createDeal: checked as boolean })
                                }
                              />
                              Crear negocio si no existe
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button
                onClick={handleSave}
                disabled={createConfiguration.isLoading || updateConfiguration.isLoading}
              >
                Guardar configuración
              </Button>
            </div>
          </>
        ) : (
          <div className="rounded-md border border-dashed border-gray-200 p-4">
            <p className="text-sm text-gray-600">
              Selecciona un tipo de negocio o pipeline para comenzar a configurar el mapeo de
              eventos de HubSpot.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HubspotCrmConfiguration;
